import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Button,
  useRecordContext,
  useDataProvider,
  TopToolbar,
  useListContext,
  useNotify,
  FunctionField,
  useRefresh,
  Pagination,
  useGetIdentity,
} from 'react-admin'
import {
  Button as MuiButton,
  Chip,
  ChipProps,
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox as MuiCheckbox,
  TextField as MuiTextField,
  FormHelperText,
} from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import QuestionnaireReviewForm from './QuestionnaireReviewForm'
import VendorDetailsDialog from './VendorDetailsDialog'
import {
  sendQuestionnaire,
  extractEmailFromVendor,
} from './SendQuestionnaireService'
import { QuestionnaireType } from '../Organisations/OrganisationRiskQuestionnaireDirectory'
import OrganisationRiskQuestionnaire from '../Organisations/OrganisationRiskQuestionnaire'
import RiskLevelEditor from './RiskLevelEditor'
import { VendorQuestionnaireStatuses } from './types'

// Quick Select Button component
const QuickSelectButton = ({
  questionnaire,
  selectedVendors,
  vendorListData,
  checkVendorsAssessmentStatus,
  handleQuickSelectClick,
}) => {
  const [isDisabled, setIsDisabled] = useState(true)
  const [disabledReason, setDisabledReason] = useState('')

  // Check if vendors meet the assessment status criteria whenever selected vendors change
  useEffect(() => {
    const checkStatus = async () => {
      // If no vendors are selected, disable the button
      if (selectedVendors.length === 0) {
        setIsDisabled(true)
        setDisabledReason('Select at least one vendor')
        return
      }

      // Check if vendors meet the assessment status criteria
      const statusCheck = checkVendorsAssessmentStatus(
        questionnaire.type,
        selectedVendors,
        vendorListData
      )

      setIsDisabled(!statusCheck.valid)
      setDisabledReason(
        statusCheck.valid ? '' : statusCheck.message || 'Cannot select vendors'
      )
    }

    checkStatus()
  }, [
    questionnaire.type,
    selectedVendors,
    vendorListData,
    checkVendorsAssessmentStatus,
  ])

  return (
    <MuiButton
      variant="contained"
      color="success"
      size="small"
      startIcon={<CheckCircleOutlineIcon />}
      disabled={isDisabled}
      onClick={(e) => {
        e.stopPropagation()
        handleQuickSelectClick(questionnaire)
      }}
      title={disabledReason}
      sx={{
        textTransform: 'none',
        borderRadius: 1,
      }}
    >
      Quick Select
    </MuiButton>
  )
}

// Create a context for sharing state variables
interface RiskManagementContextType {
  vendorDetailsDialogOpen: boolean
  setVendorDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedVendorId: string | number | null
  setSelectedVendorId: React.Dispatch<
    React.SetStateAction<string | number | null>
  >
}

const RiskManagementContext = createContext<RiskManagementContextType>({
  vendorDetailsDialogOpen: false,
  setVendorDetailsDialogOpen: () => {},
  selectedVendorId: null,
  setSelectedVendorId: () => {},
})

// Default risk level ranges for the summary panel (will be replaced by API data)
const DEFAULT_RISK_LEVELS = [
  { level: 'Unknown', color: '#9e9e9e', range: '0-100' },
]

// Interface for risk level data from API
interface RiskLevel {
  level: string
  color: string
  low_range: number
  high_range: number
}

// RiskSummary component for the top of the page
const RiskSummary = ({
  summaryData,
  riskLevelsData,
  loading,
  onEditRiskLevels,
  organisationId,
}: {
  summaryData: {
    vendor_inventory: number
    assessment_status: { completed: number; total: number; percentage: number }
  } | null
  riskLevelsData: any[]
  loading: boolean
  onEditRiskLevels: () => void
  organisationId: number
}) => {
  const [riskLevels, setRiskLevels] = useState<
    Array<{ level: string; color: string; range: string }>
  >([])

  // Update risk levels when riskLevelsData changes
  useEffect(() => {
    if (riskLevelsData && riskLevelsData.length > 0) {
      // Transform the API risk levels to the format expected by the component
      const formattedRiskLevels = riskLevelsData.map((riskLevel: any) => ({
        level: riskLevel.level,
        color: riskLevel.color,
        range: `${riskLevel.low_range}-${riskLevel.high_range}`,
      }))
      setRiskLevels(formattedRiskLevels)
    } else {
      // If no risk levels are returned, use the default ones
      setRiskLevels(DEFAULT_RISK_LEVELS)
    }
  }, [riskLevelsData])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ mb: '10px' }}>
      <Grid container spacing={2}>
        {/* Card 1: Vendor Inventory */}
        <Grid item xs={12} sm={3}>
          <Paper
            sx={{
              p: 3,
              height: '100%',
              bgcolor: '#4caf50',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="regular"
              align="left"
              sx={{ opacity: 0.9 }}
            >
              Vendor Inventory
            </Typography>
            <Typography
              variant="h3"
              component="div"
              sx={{ mt: 1, fontWeight: 'bold' }}
            >
              {summaryData?.vendor_inventory || 0}
            </Typography>
          </Paper>
        </Grid>

        {/* Card 2: Assessment Status */}
        <Grid item xs={12} sm={3}>
          <Paper
            sx={{
              p: 3,
              height: '100%',
              bgcolor: 'white',
              color: 'black',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              borderRadius: 2,
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="regular"
              align="left"
              sx={{ color: 'text.secondary' }}
            >
              Assessment Status
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                my: 2,
                flexGrow: 1,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                }}
              >
                {/* Background circle (track) */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={100}
                  thickness={8}
                  sx={{
                    color: '#e5e7eb', // Light gray for the background/track
                    position: 'absolute',
                    left: 0,
                  }}
                />
                {/* Foreground circle (progress) */}
                <CircularProgress
                  variant="determinate"
                  value={summaryData?.assessment_status.percentage || 0}
                  size={100}
                  thickness={8}
                  sx={{
                    color: '#10b981', // Green color for the filled part
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
                {/* Percentage text */}
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="h5"
                    component="div"
                    color="text.primary"
                    fontWeight="bold"
                  >
                    {`${summaryData?.assessment_status.percentage || 0}%`}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography
              variant="caption"
              align="center"
              sx={{ color: 'text.secondary', mt: 1 }}
            >
              Completed
            </Typography>
          </Paper>
        </Grid>

        {/* Card 3: Risk Levels */}
        <Grid item xs={12} sm={6}>
          <Paper
            sx={{
              p: 3,
              height: '100%',
              bgcolor: 'white',
              color: 'black',
              borderRadius: 2,
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="regular"
                align="left"
                sx={{ color: 'text.secondary', fontSize: '14px' }}
              >
                Risk Levels
              </Typography>
              <IconButton
                size="small"
                onClick={onEditRiskLevels}
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  riskLevels.length > 5 ? 'repeat(2, 1fr)' : '1fr',
                gap: 1,
              }}
            >
              {riskLevels.map((level) => (
                <Box
                  key={level.level}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: level.color,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 'medium',
                      fontSize: '14px',
                    }}
                  >
                    {level.level}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    {level.range}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

// Create a context for sharing vendor details state and functions
interface VendorDetailsContextType {
  vendorDetailsDialogOpen: boolean
  setVendorDetailsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedVendorQuestionnaires: Array<any>
  setSelectedVendorQuestionnaires: React.Dispatch<
    React.SetStateAction<Array<any>>
  >
  selectedVendorDetails: any
  setSelectedVendorDetails: React.Dispatch<React.SetStateAction<any>>
  selectedVendorQuestionnaire: any
  setSelectedVendorQuestionnaire: React.Dispatch<React.SetStateAction<any>>
  loadingVendorDetails: boolean
  setLoadingVendorDetails: React.Dispatch<React.SetStateAction<boolean>>
  loadVendorQuestionnaires: (vendorId: number) => Promise<void>
}

const VendorDetailsContext = createContext<
  VendorDetailsContextType | undefined
>(undefined)

const useVendorDetailsContext = () => {
  const context = useContext(VendorDetailsContext)
  if (context === undefined) {
    throw new Error(
      'useVendorDetailsContext must be used within a VendorDetailsProvider'
    )
  }
  return context
}

// Custom field to display risk level with appropriate color
const RiskLevelField = ({
  source,
  label,
  riskLevelsData,
  loading: riskLevelsLoading,
}: {
  source: string
  label?: string
  riskLevelsData: any[]
  loading: boolean
}) => {
  const record = useRecordContext()
  const [riskLevelData, setRiskLevelData] = useState<{
    level: string
    color: string
  }>({
    level: 'Unknown',
    color: '#9e9e9e', // Gray
  })

  useEffect(() => {
    if (!record) return

    // Check if risk_level_name and risk_level_color are directly available in risk_assessment
    if (
      record.risk_assessment?.risk_level_name &&
      record.risk_assessment?.risk_level_color
    ) {
      setRiskLevelData({
        level: record.risk_assessment.risk_level_name,
        color: record.risk_assessment.risk_level_color,
      })
      return
    }

    // Fallback to the old method if the new fields aren't available
    if (riskLevelsLoading || !riskLevelsData || riskLevelsData.length === 0)
      return

    // Find the matching risk level for this record
    const riskLevel = record.risk_assessment?.risk_level || 3 // Default to high risk (3) if not set

    // Find the risk level that matches this ID or use default mapping
    const matchingLevel = riskLevelsData.find(
      (level: any) => level.id === riskLevel
    )

    if (matchingLevel) {
      setRiskLevelData({
        level: matchingLevel.level,
        color: matchingLevel.color,
      })
    } else if (riskLevelsData.length > 0) {
      // If no match found, use the first risk level from the API
      const firstLevel = riskLevelsData[0]
      setRiskLevelData({ level: firstLevel.level, color: firstLevel.color })
    } else {
      // Default to Unknown if no risk levels are available
      setRiskLevelData({ level: 'Unknown', color: '#9e9e9e' })
    }
  }, [record, riskLevelsData, riskLevelsLoading])

  if (!record) return null
  if (riskLevelsLoading && !record.risk_assessment?.risk_level_name)
    return <CircularProgress size={20} />

  return (
    <Typography
      variant="body2"
      sx={{
        bgcolor: riskLevelData.color,
        color: 'white',
        px: 2,
        py: 0.5,
        borderRadius: 1,
        fontWeight: 'medium',
        display: 'inline-block',
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {riskLevelData.level}
    </Typography>
  )
}

// Custom field to display compliance score
const ComplianceScoreField = ({
  source,
  label,
  riskLevelsData,
  loading: riskLevelsLoading,
}: {
  source: string
  label?: string
  riskLevelsData: any[]
  loading: boolean
}) => {
  const record = useRecordContext()
  const [scoreColor, setScoreColor] = useState('#f44336') // Default to red

  // Compliance score will be NULL=0
  const score = record?.risk_assessment?.compliance_score || 0

  useEffect(() => {
    if (!record) return

    // Check if risk_level_color is directly available in risk_assessment
    if (record.risk_assessment?.risk_level_color) {
      setScoreColor(record.risk_assessment.risk_level_color)
      return
    }

    // Fallback to the old method if the new field isn't available
    if (riskLevelsLoading || !riskLevelsData || riskLevelsData.length === 0)
      return

    // Find the risk level that matches the compliance score
    const matchingLevel = riskLevelsData.find(
      (level: any) => score >= level.low_range && score <= level.high_range
    )

    if (matchingLevel) {
      setScoreColor(matchingLevel.color)
    } else if (riskLevelsData.length > 0) {
      // If no match found, use the first risk level from the API
      const firstLevel = riskLevelsData[0]
      setScoreColor(firstLevel.color)
    } else {
      // Gray for unknown risk
      setScoreColor('#9e9e9e')
    }
  }, [record, riskLevelsData, riskLevelsLoading, score])

  if (!record) return null
  if (riskLevelsLoading && !record.risk_assessment?.risk_level_color)
    return <CircularProgress size={20} />

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 'bold',
        color: 'black', // Always show in black
        fontSize: '1rem',
      }}
    >
      {score}
    </Typography>
  )
}

// Define enum for assessment statuses to match the backend
enum VendorAssessmentStatuses {
  None = 0,
  Prequalifying = 1,
  Prequalified = 2,
  Pending = 3,
  Completed = 4,
  ClarificationRequested = 5,
}

// Custom field to display assessment status
const AssessmentStatusField = ({
  source,
  label,
}: {
  source: string
  label?: string
}) => {
  const record = useRecordContext()
  if (!record) return null

  let status = 'None'
  let color = '#9e9e9e' // Gray

  // Use the enum for better readability and maintainability
  if (
    record.risk_assessment?.assessment_status ===
    VendorAssessmentStatuses.Prequalifying
  ) {
    status = 'Prequalifying'
    color = '#2196f3' // Blue
  } else if (
    record.risk_assessment?.assessment_status ===
    VendorAssessmentStatuses.Prequalified
  ) {
    status = 'Prequalified'
    color = '#8bc34a' // Light Green
  } else if (
    record.risk_assessment?.assessment_status ===
    VendorAssessmentStatuses.Pending
  ) {
    status = 'Pending'
    color = '#ff9800' // Orange
  } else if (
    record.risk_assessment?.assessment_status ===
    VendorAssessmentStatuses.Completed
  ) {
    status = 'Completed'
    color = '#4caf50' // Green
  } else if (
    record.risk_assessment?.assessment_status ===
    VendorAssessmentStatuses.ClarificationRequested
  ) {
    status = 'Clarification Requested'
    color = '#ff5722' // Deep Orange
  }

  return (
    <Typography
      variant="body2"
      sx={{
        bgcolor: color,
        color: 'white',
        px: 2,
        py: 0.5,
        borderRadius: 1,
        fontWeight: 'medium',
        display: 'inline-block',
      }}
    >
      {status}
    </Typography>
  )
}

// Custom field to display last assessment date
const LastAssessmentField = ({
  source,
  label,
}: {
  source: string
  label?: string
}) => {
  const record = useRecordContext()
  if (!record || !record.risk_assessment?.last_assessment) {
    return <span>Not assessed yet</span>
  }

  // Create a new record with the last_assessment field at the top level
  const recordWithLastAssessment = {
    ...record,
    last_assessment: record.risk_assessment.last_assessment,
  }

  return (
    <DateField source="last_assessment" record={recordWithLastAssessment} />
  )
}

// Custom header component for the select all checkbox
const SelectAllHeader = ({
  selectedVendors,
  setSelectedVendors,
  setVendorListData,
}: {
  selectedVendors: number[]
  setSelectedVendors: React.Dispatch<React.SetStateAction<number[]>>
  setVendorListData: React.Dispatch<React.SetStateAction<any[]>>
}) => {
  const { data, total } = useListContext()
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  // Update the vendor list data when it changes
  useEffect(() => {
    if (data && data.length > 0) {
      setVendorListData(data)
    }
  }, [data, setVendorListData])

  // Calculate if all visible vendors are selected
  const allVisibleSelected =
    data &&
    data.length > 0 &&
    data.every((vendor) => selectedVendors.includes(vendor.id))

  // Calculate if some but not all visible vendors are selected
  const someVisibleSelected =
    data &&
    data.length > 0 &&
    data.some((vendor) => selectedVendors.includes(vendor.id)) &&
    !allVisibleSelected

  // Update selectAllChecked state when allVisibleSelected changes
  useEffect(() => {
    if (allVisibleSelected && !selectAllChecked) {
      setSelectAllChecked(true)
    }
  }, [allVisibleSelected, selectAllChecked])

  return (
    <MuiCheckbox
      checked={selectAllChecked}
      indeterminate={someVisibleSelected && !selectAllChecked}
      onChange={() => {
        if (data && data.length > 0) {
          if (selectAllChecked) {
            // Deselect visible vendors
            const visibleIds = data.map((vendor) => vendor.id)
            setSelectedVendors(
              selectedVendors.filter((id) => !visibleIds.includes(id))
            )
            setSelectAllChecked(false)
          } else {
            // Select all visible vendors
            const visibleIds = data.map((vendor) => vendor.id)
            const newSelectedVendors = [...selectedVendors]

            // Add any visible vendors that aren't already selected
            visibleIds.forEach((id) => {
              if (!newSelectedVendors.includes(id)) {
                newSelectedVendors.push(id)
              }
            })

            setSelectedVendors(newSelectedVendors)
            setSelectAllChecked(true)
          }
        }
      }}
    />
  )
}

// Custom field to display assessor name
const AssessorField = ({
  source,
  label,
}: {
  source: string
  label?: string
}) => {
  const record = useRecordContext()

  if (!record) return null

  // Check if the record has a risk assessment with a risk_assessor_name
  if (record.risk_assessment && record.risk_assessment.risk_assessor_name) {
    return <span>{record.risk_assessment.risk_assessor_name}</span>
  }

  // If no risk_assessor_name is found, display "Not assigned"
  return <span>Not assigned</span>
}

// Custom field to display questionnaires button with email icon
const QuestionnairesField = ({
  source,
  label,
}: {
  source: string
  label?: string
}) => {
  const record = useRecordContext()
  const { setVendorDetailsDialogOpen, setSelectedVendorId } = useContext(
    RiskManagementContext
  )

  if (!record) return null

  const handleClick = () => {
    // Open the vendor details dialog instead of navigating to a new page
    setSelectedVendorId(record.id)
    setVendorDetailsDialogOpen(true)
  }

  return (
    <Button
      label="View"
      onClick={handleClick}
      title="View Completed Questionnaire"
    >
      <VisibilityIcon />
    </Button>
  )
}

// Empty ListActions component for the vendor list
const VendorListActions = () => {
  return (
    <TopToolbar
      sx={{
        height: 0,
        minHeight: 0,
        padding: 0,
        overflow: 'hidden',
      }}
    />
  )
}

const RiskManagement = () => {
  // Define filters inside the component
  const vendorFilters = []
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const { identity } = useGetIdentity()
  // State to store the vendor data from the list
  const [vendorListData, setVendorListData] = useState<any[]>([])
  const [summaryData, setSummaryData] = useState<any>(null)
  const [riskLevels, setRiskLevels] = useState<Array<any>>([])
  const [loading, setLoading] = useState(true)
  const [questionnaires, setQuestionnaires] = useState<Array<any>>([])
  const [templatesExpanded, setTemplatesExpanded] = useState(false)
  const [reviewQuestionnairesExpanded, setReviewQuestionnairesExpanded] =
    useState(false)
  const [activeTemplateType, setActiveTemplateType] =
    useState<QuestionnaireType | null>(null)
  const [activeReviewType, setActiveReviewType] =
    useState<QuestionnaireType | null>(null)
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false)
  const [loadingReviewQuestionnaires, setLoadingReviewQuestionnaires] =
    useState(false)
  const [reviewQuestionnaires, setReviewQuestionnaires] = useState<Array<any>>(
    []
  )
  const [selectedReviewQuestionnaire, setSelectedReviewQuestionnaire] =
    useState<any>(null)
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [riskLevelEditorOpen, setRiskLevelEditorOpen] = useState(false)

  // State for vendor details dialog
  const [vendorDetailsDialogOpen, setVendorDetailsDialogOpen] = useState(false)
  const [selectedVendorId, setSelectedVendorId] = useState<
    string | number | null
  >(null)

  // State for selected vendors and quick select modal
  const [selectedVendors, setSelectedVendors] = useState<number[]>([])
  const [quickSelectModalOpen, setQuickSelectModalOpen] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<any>(null)
  const [users, setUsers] = useState<any[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [selectedAssessor, setSelectedAssessor] = useState<string>('')
  const [emailFields, setEmailFields] = useState<
    { value: string; label: string }[]
  >([])
  const [selectedEmailField, setSelectedEmailField] = useState<string>('')
  const [sendingQuestionnaires, setSendingQuestionnaires] = useState(false)
  const [sendingProgress, setSendingProgress] = useState(0)
  const [totalToSend, setTotalToSend] = useState(0)

  // State for questionnaire actions
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [questionnaireToView, setQuestionnaireToView] = useState<any>(null)
  const [questionnaireToEdit, setQuestionnaireToEdit] = useState<any>(null)
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState<
    number | null
  >(null)
  const [newFormName, setNewFormName] = useState('')
  const [newFormDescription, setNewFormDescription] = useState('')
  const [newFormType, setNewFormType] = useState<QuestionnaireType>(
    QuestionnaireType.RISK
  )
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(false)

  // State for filter visibility
  const [isFilterVisible, setIsFilterVisible] = useState(false)

  // State to track the total number of vendors
  const [vendorCount, setVendorCount] = useState(0)

  // Define state variables for filter values
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('')
  const [assessmentStatusFilter, setAssessmentStatusFilter] =
    useState<string>('')
  const [assessorFilter, setAssessorFilter] = useState<string>('')
  const [lastAssessmentFilter, setLastAssessmentFilter] = useState<string>('')

  // Define initial filter values to include risk assessment data
  const [filterValues, setFilterValues] = useState({
    include_risk_assessment: true,
    risk_level: '',
    assessment_status: '',
    risk_assessor_id: '',
    last_assessment: '',
  })

  // Function to apply filters
  const applyFilters = () => {
    setFilterValues({
      include_risk_assessment: true,
      risk_level: riskLevelFilter,
      assessment_status: assessmentStatusFilter,
      risk_assessor_id: assessorFilter,
      last_assessment: lastAssessmentFilter,
    })
    refresh() // Refresh the vendors list to apply the filters
  }

  // Define interfaces for vendor form structure
  interface VendorFormField {
    source?: string
    label?: string
    [key: string]: any
  }

  interface VendorFormSection {
    title?: string
    fields?: VendorFormField[]
    [key: string]: any
  }

  // Load users with appropriate permissions
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true)
    try {
      // Get all users
      const { data: allUsers } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })

      // Filter users with EDIT_TRANSACTIONS permission (128) - for users who can edit vendors
      const relevantUsers = allUsers.filter(
        (user) => (user.permission_flags & 128) === 128
      )

      setUsers(relevantUsers)
    } catch (error) {
      console.error('Error loading users:', error)
      notify('Error loading users', { type: 'error' })
    } finally {
      setLoadingUsers(false)
    }
  }, [dataProvider, notify])

  // Load email fields from vendor form
  const loadEmailFields = useCallback(async () => {
    try {
      // Load vendor form for the organisation
      const response = await dataProvider.custom('vendor-forms/organisations', {
        method: 'GET',
      })

      let vendorForm: VendorFormSection[] = []

      // Check if we got a list of vendor forms or a single vendor form
      if (Array.isArray(response.data)) {
        // We got a list of vendor forms, use the first one if available
        if (response.data.length > 0 && response.data[0]?.vendor_form) {
          vendorForm =
            (response.data[0].vendor_form as VendorFormSection[]) || []
        }
      } else if (response.data?.vendor_form) {
        // We got a single vendor form
        vendorForm = response.data.vendor_form as VendorFormSection[]
      }

      // Extract email fields from the vendor form
      const emailFieldsList: { value: string; label: string }[] = []

      if (vendorForm && vendorForm.length > 0) {
        vendorForm.forEach((section: VendorFormSection) => {
          if (
            section?.fields &&
            Array.isArray(section.fields) &&
            section.fields.length > 0
          ) {
            section.fields.forEach((field: VendorFormField) => {
              // Check if field is an email field (contains 'email' in source or label)
              if (
                field.source &&
                (field.source.toLowerCase().includes('email') ||
                  (field.label && field.label.toLowerCase().includes('email')))
              ) {
                emailFieldsList.push({
                  value: field.source,
                  label: `${field.label || field.source} (${
                    section.title || 'Unnamed Section'
                  })`,
                })
              }
            })
          }
        })
      }

      setEmailFields(emailFieldsList)

      // Set default email field if available
      if (emailFieldsList.length > 0) {
        setSelectedEmailField(emailFieldsList[0].value)
      }
    } catch (error) {
      console.error('Error loading email fields:', error)
      notify('Error loading email fields', { type: 'error' })
    }
  }, [dataProvider, notify])

  // Fetch summary data and risk levels
  const fetchSummaryData = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch data from the API
      const { data } = await dataProvider.custom('risk-assessment/summary', {
        method: 'GET',
      })

      // Store the entire summary data
      setSummaryData(data)

      // Extract risk levels for use in child components
      if (data.risk_levels && data.risk_levels.length > 0) {
        setRiskLevels(data.risk_levels)
      } else {
        setRiskLevels([])
      }
    } catch (error) {
      console.error('Error fetching summary data:', error)
      setSummaryData(null)
      setRiskLevels([])
      notify('Error loading risk assessment data', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [dataProvider, notify])

  // Handle risk levels save - fetch summary data, reset filters, and refresh the vendor list
  const handleRiskLevelsSave = async () => {
    await fetchSummaryData()

    // Reset all filter state variables
    setRiskLevelFilter('')
    setAssessmentStatusFilter('')
    setAssessorFilter('')
    setLastAssessmentFilter('')

    // Reset the filterValues object
    setFilterValues({
      include_risk_assessment: true,
      risk_level: '',
      assessment_status: '',
      risk_assessor_id: '',
      last_assessment: '',
    })

    refresh() // Refresh the vendors list to update risk levels with reset filters
  }

  // Load questionnaires assigned to the current user for review
  const loadReviewQuestionnaires = useCallback(async () => {
    if (!identity?.id) return

    setLoadingReviewQuestionnaires(true)
    try {
      // Fetch questionnaires assigned to the current user
      const { data } = await dataProvider.custom(
        `questionnaires/vendors/assigned/${identity.id}`,
        {
          method: 'GET',
        }
      )

      setReviewQuestionnaires(data || [])
    } catch (error) {
      console.error('Error loading questionnaires for review:', error)
      notify('Error loading questionnaires for review', { type: 'error' })
      setReviewQuestionnaires([])
    } finally {
      setLoadingReviewQuestionnaires(false)
    }
  }, [dataProvider, identity, notify])

  // Fetch summary data and risk levels once when the component mounts
  useEffect(() => {
    // Create a variable to track if the component is still mounted
    let isMounted = true

    const loadInitialData = async () => {
      await fetchSummaryData()

      // Load users and email fields for the quick select modal
      if (isMounted) {
        await loadUsers()
        await loadEmailFields()
        await loadReviewQuestionnaires()
      }
    }

    loadInitialData()

    // Cleanup function to prevent state updates if the component unmounts
    return () => {
      isMounted = false
    }
  }, [
    dataProvider,
    identity,
    fetchSummaryData,
    loadEmailFields,
    loadReviewQuestionnaires,
    loadUsers,
  ])

  // Fetch questionnaires
  const fetchQuestionnaires = useCallback(async () => {
    setLoadingQuestionnaires(true)
    try {
      // Fetch questionnaires from the API
      const { data } = await dataProvider.custom(
        'questionnaires/organisations/current',
        { method: 'GET' }
      )

      // Convert string type values to enum values
      const formattedQuestionnaires = (data || []).map((questionnaire) => {
        if (questionnaire.type) {
          return {
            ...questionnaire,
            type:
              questionnaire.type === 'preliminary'
                ? QuestionnaireType.PRELIMINARY
                : QuestionnaireType.RISK,
          }
        }
        return questionnaire
      })
      setQuestionnaires(formattedQuestionnaires)
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
      notify('Error fetching questionnaires', { type: 'error' })
      setQuestionnaires([])
    } finally {
      setLoadingQuestionnaires(false)
    }
  }, [dataProvider, notify])

  // Reset the create form
  const resetCreateForm = () => {
    setNewFormName('')
    setNewFormDescription('')
  }

  // Handle viewing a questionnaire
  const handleViewQuestionnaire = (questionnaire: any) => {
    setQuestionnaireToView(questionnaire)
    setIsViewModalOpen(true)
  }

  // Handle editing a questionnaire
  const handleEditForm = (questionnaire: any) => {
    setQuestionnaireToEdit(questionnaire)
    setIsEditModalOpen(true)
  }

  // Handle duplicating a questionnaire
  const handleDuplicateQuestionnaire = async (questionnaireId: number) => {
    try {
      setIsDuplicating(true)
      const endpoint = `questionnaires/${questionnaireId}/organisations/current/duplicate`

      const response = await dataProvider.custom(endpoint, {
        method: 'POST',
      })

      notify('Questionnaire duplicated successfully', { type: 'success' })

      // Get the duplicated questionnaire from the response
      const duplicatedQuestionnaire = response.data

      // Convert type if needed
      if (duplicatedQuestionnaire && duplicatedQuestionnaire.type) {
        duplicatedQuestionnaire.type =
          duplicatedQuestionnaire.type === 'preliminary'
            ? QuestionnaireType.PRELIMINARY
            : QuestionnaireType.RISK
      }

      // Reload the questionnaires
      await fetchQuestionnaires()

      // Open the duplicated questionnaire for editing
      if (duplicatedQuestionnaire) {
        setQuestionnaireToEdit(duplicatedQuestionnaire)
        setIsEditModalOpen(true)
      }
    } catch (error) {
      console.error('Error duplicating questionnaire:', error)
      notify('Error duplicating questionnaire', { type: 'error' })
    } finally {
      setIsDuplicating(false)
    }
  }

  // Show delete confirmation dialog
  const showDeleteConfirmation = (formId: number) => {
    setQuestionnaireToDelete(formId)
    setIsDeleteDialogOpen(true)
  }

  // Handle deleting a questionnaire
  const handleDeleteForm = async () => {
    if (!questionnaireToDelete) return

    try {
      setIsDeleting(true)
      const endpoint = `questionnaires/${questionnaireToDelete}/organisations/current`

      await dataProvider.custom(endpoint, {
        method: 'DELETE',
      })

      notify('Questionnaire deleted successfully', { type: 'success' })

      // Update the local state to remove the deleted form
      setQuestionnaires(
        questionnaires.filter((form) => form.id !== questionnaireToDelete)
      )

      // Close the dialog
      setIsDeleteDialogOpen(false)
      setQuestionnaireToDelete(null)
    } catch (error) {
      console.error('Error deleting questionnaire:', error)
      notify('Error deleting questionnaire', { type: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  // Handle creating a new questionnaire
  const handleCreateForm = async () => {
    if (!newFormName.trim()) {
      notify('Form name is required', { type: 'warning' })
      return
    }

    try {
      setIsCreating(true)
      const endpoint = 'questionnaires/organisations/current'

      const payload = {
        name: newFormName,
        description: newFormDescription,
        type: newFormType,
        questionnaire: [], // Initialize with an empty questionnaire
        expiry_minutes: 60, // Default to 60 minutes (1 hour)
      }

      const createResponse = await dataProvider.custom(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      notify('Questionnaire created successfully', { type: 'success' })
      setIsCreateDialogOpen(false)
      resetCreateForm()

      // Reload the questionnaires
      await fetchQuestionnaires()

      // Find the newly created questionnaire
      if (createResponse.data && createResponse.data.id) {
        const newQuestionnaire = createResponse.data
        // Convert type if needed
        if (newQuestionnaire.type) {
          newQuestionnaire.type =
            newQuestionnaire.type === 'preliminary'
              ? QuestionnaireType.PRELIMINARY
              : QuestionnaireType.RISK
        }
        // Open the edit form for the newly created questionnaire
        setQuestionnaireToEdit(newQuestionnaire)
        setIsEditModalOpen(true)
      }
    } catch (error) {
      console.error('Error creating questionnaire:', error)
      // Try to extract the specific error message from the backend response
      let errorMessage = 'Error creating questionnaire'
      if (error && typeof error === 'object') {
        if (
          'body' in error &&
          error.body &&
          typeof error.body === 'object' &&
          'detail' in error.body
        ) {
          errorMessage = error.body.detail as string
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message
        }
      }
      notify(errorMessage, { type: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setQuestionnaireToEdit(null)
    // Refresh the list after editing
    refresh()
    // Update the questionnaire directory listing
    fetchQuestionnaires()
  }

  // Handle closing the view modal
  const handleCloseViewModal = () => {
    setIsViewModalOpen(false)
    setQuestionnaireToView(null)
  }

  // Check if vendors meet the assessment status criteria for a questionnaire type
  const checkVendorsAssessmentStatus = (
    questionnaireType,
    vendorIds,
    listData
  ) => {
    try {
      // Use the vendor data from the list context instead of making API calls
      const vendors = vendorIds
        .map((id) => listData.find((vendor) => vendor.id === id))
        .filter(Boolean)

      console.log('Checking vendors:', vendors)
      console.log('Questionnaire type:', questionnaireType)

      if (questionnaireType === QuestionnaireType.PRELIMINARY) {
        // For Preliminary Questionnaires, check if all vendors have assessment_status = 0 (None)
        const invalidVendors = vendors.filter(
          (vendor) =>
            vendor.risk_assessment &&
            Number(vendor.risk_assessment.assessment_status) !== 0
        )

        console.log('Preliminary - Invalid vendors:', invalidVendors)
        console.log(
          'Preliminary - Invalid vendors assessment status:',
          invalidVendors.map((v) => v.risk_assessment?.assessment_status)
        )
        console.log(
          'Preliminary - All vendors assessment status:',
          vendors.map((v) => ({
            id: v.id,
            name: v.name,
            status: v.risk_assessment
              ? Number(v.risk_assessment.assessment_status)
              : 'No risk assessment',
          }))
        )

        if (invalidVendors.length > 0) {
          return {
            valid: false,
            message:
              'Quick Select for Preliminary Questionnaires is only allowed for vendors with assessment status "None"',
          }
        }
      } else if (questionnaireType === QuestionnaireType.RISK) {
        // For Risk Assessments, check if all vendors have assessment_status = 2 (Prequalified)
        const invalidVendors = vendors.filter(
          (vendor) =>
            !vendor.risk_assessment ||
            (Number(vendor.risk_assessment.assessment_status) !== 2 &&
              Number(vendor.risk_assessment.assessment_status) !== 4)
        )

        console.log('Risk - Invalid vendors:', invalidVendors)
        console.log(
          'Risk - Invalid vendors assessment status:',
          invalidVendors.map((v) => v.risk_assessment?.assessment_status)
        )
        console.log(
          'Risk - All vendors assessment status:',
          vendors.map((v) => ({
            id: v.id,
            name: v.name,
            status: v.risk_assessment
              ? Number(v.risk_assessment.assessment_status)
              : 'No risk assessment',
          }))
        )

        if (invalidVendors.length > 0) {
          return {
            valid: false,
            message:
              'Quick Select for Risk Assessments is only allowed for vendors with assessment status "Prequalified"',
          }
        }
      }

      return { valid: true }
    } catch (error) {
      console.error('Error checking vendor assessment status:', error)
      return {
        valid: false,
        message: 'Error checking vendor assessment status',
      }
    }
  }

  // Handle Quick Select button click
  const handleQuickSelectClick = (questionnaire) => {
    // Set the selected questionnaire
    setSelectedQuestionnaire(questionnaire)

    // Check if vendors meet the assessment status criteria
    const statusCheck = checkVendorsAssessmentStatus(
      questionnaire.type,
      selectedVendors,
      vendorListData
    )

    if (!statusCheck.valid) {
      notify(statusCheck.message, { type: 'warning' })
      return
    }

    // Open the Quick Select modal if criteria are met
    setQuickSelectModalOpen(true)
  }

  // Handle sending questionnaires to selected vendors
  const handleSendQuestionnaires = async () => {
    if (
      !selectedQuestionnaire ||
      selectedVendors.length === 0 ||
      !selectedAssessor ||
      !selectedEmailField
    ) {
      notify('Please select all required fields', { type: 'warning' })
      return
    }

    // Check if vendors meet the assessment status criteria
    const statusCheck = checkVendorsAssessmentStatus(
      selectedQuestionnaire.type,
      selectedVendors,
      vendorListData
    )

    if (!statusCheck.valid) {
      notify(statusCheck.message, { type: 'warning' })
      return
    }

    setSendingQuestionnaires(true)
    setSendingProgress(0)
    setTotalToSend(selectedVendors.length)

    try {
      // Use the shared sendQuestionnaire function
      const result = await sendQuestionnaire({
        dataProvider,
        questionnaireId: selectedQuestionnaire.id,
        vendorIds: selectedVendors,
        assessorId: selectedAssessor,
        emailField: selectedEmailField,
        notify,
        extractEmailAddress: extractEmailFromVendor,
        onProgress: (current, total) => {
          setSendingProgress(current)
        },
      })

      // Show notification with results
      if (result.successCount > 0) {
        notify(
          `Successfully sent ${result.successCount} questionnaire(s)${
            result.errorCount > 0 ? `, ${result.errorCount} failed` : ''
          }`,
          { type: result.errorCount > 0 ? 'warning' : 'success' }
        )
      } else {
        notify('Failed to send questionnaires', { type: 'error' })
      }

      // Close modal and reset selection
      setQuickSelectModalOpen(false)
      setSelectedVendors([])

      // Refresh the list of questionnaires assigned to the current user
      loadReviewQuestionnaires()

      // Refresh the vendor list to show updated assessment status
      refresh()
    } catch (error) {
      console.error('Error sending questionnaires:', error)
      notify('Error sending questionnaires', { type: 'error' })
    } finally {
      setSendingQuestionnaires(false)
      // Reset progress after a short delay to show 100% completion
      setTimeout(() => {
        setSendingProgress(0)
        setTotalToSend(0)
      }, 1000)
    }
  }

  // Load questionnaires when the component mounts
  useEffect(() => {
    // Create a variable to track if the component is still mounted
    let isMounted = true

    const loadQuestionnaires = async () => {
      setLoadingQuestionnaires(true)
      try {
        // Fetch questionnaires from the API
        const { data } = await dataProvider.custom(
          'questionnaires/organisations/current',
          { method: 'GET' }
        )

        // Only update state if the component is still mounted
        if (isMounted) {
          // Convert string type values to enum values
          const formattedQuestionnaires = (data || []).map((questionnaire) => {
            if (questionnaire.type) {
              return {
                ...questionnaire,
                type:
                  questionnaire.type === 'preliminary'
                    ? QuestionnaireType.PRELIMINARY
                    : QuestionnaireType.RISK,
              }
            }
            return questionnaire
          })
          setQuestionnaires(formattedQuestionnaires)
        }
      } catch (error) {
        console.error('Error fetching questionnaires:', error)
        if (isMounted) {
          setQuestionnaires([])
        }
      } finally {
        if (isMounted) {
          setLoadingQuestionnaires(false)
        }
      }
    }

    loadQuestionnaires()

    // Cleanup function to prevent state updates if the component unmounts
    return () => {
      isMounted = false
    }
  }, [dataProvider])

  return (
    <section>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            3rd-Party Risk Management
          </Typography>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '50%',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <ShieldCheckIcon className="h-6" />
          </Box>
        </Box>
      </Box>

      {/* Summary Panel */}
      <RiskSummary
        summaryData={summaryData}
        riskLevelsData={riskLevels}
        loading={loading}
        onEditRiskLevels={() => setRiskLevelEditorOpen(true)}
        organisationId={summaryData?.organisation_id || 0}
      />

      {/* Risk Level Editor Dialog */}
      <RiskLevelEditor
        open={riskLevelEditorOpen}
        onClose={() => setRiskLevelEditorOpen(false)}
        onSave={handleRiskLevelsSave}
        organisationId={summaryData?.organisation_id || 0}
        initialRiskLevelsData={riskLevels}
      />

      <Paper
        sx={{
          borderRadius: 2,
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          mb: 4,
          mt: 4,
        }}
      >
        {/* Enhanced Filter Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fefefe',
          }}
        >
          <Box
            className="filter-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '18px',
              fontWeight: 600,
              color: '#1e293b',
            }}
          >
            <FilterListIcon sx={{ color: '#64748b', width: 20, height: 20 }} />
            <Typography
              variant="h6"
              sx={{ fontSize: 'inherit', fontWeight: 'inherit', m: 0 }}
            >
              Vendor Assessment Status
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              fontSize: '13px',
              color: '#64748b',
            }}
          >
            {/* Vendor count in header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: 'inherit', fontWeight: 600 }}
              >
                {vendorCount} vendors found
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
              Filters active:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#3b82f6',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: '12px',
                fontWeight: 500,
                minWidth: '20px',
                textAlign: 'center',
              }}
            >
              {(riskLevelFilter ? 1 : 0) +
                (assessmentStatusFilter ? 1 : 0) +
                (assessorFilter ? 1 : 0) +
                (lastAssessmentFilter ? 1 : 0)}
            </Box>
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setRiskLevelFilter('')
                setAssessmentStatusFilter('')
                setAssessorFilter('')
                setLastAssessmentFilter('')
                setTimeout(() => {
                  applyFilters()
                }, 0)
              }}
              sx={{
                color: '#6b7280',
                textDecoration: 'none',
                fontSize: '13px',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                },
              }}
            >
              Clear all
            </Box>
            <IconButton
              onClick={() => setIsFilterVisible(!isFilterVisible)}
              size="small"
              color="primary"
              aria-label="toggle filter"
              sx={{ ml: 1 }}
            >
              {isFilterVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Refined Filter Controls */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #e5e7eb',
            display: isFilterVisible ? 'grid' : 'none',
            gridTemplateColumns: 'repeat(5, 1fr)',
            width: '100%',
            gridTemplateRows: 'repeat(2, auto)',
            gap: 2,
            alignItems: 'start',
            backgroundColor: 'white',
          }}
        >
          {/* Risk Level Filter - Label (Row 1) */}
          <Box
            className="filter-group-label"
            sx={{ gridRow: 1, gridColumn: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#374151',
                mb: '2px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Risk Level
            </Typography>
          </Box>

          {/* Risk Level Filter - Dropdown (Row 2) */}
          <Box
            className="filter-group-input"
            sx={{ gridRow: 2, gridColumn: 1 }}
          >
            <Select
              value={riskLevelFilter}
              onChange={(e) => setRiskLevelFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                ...(riskLevelFilter && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  backgroundColor: '#eff6ff',
                }),
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                },
              }}
            >
              <MenuItem value="">All levels</MenuItem>
              {riskLevels.map((level) => {
                const levelId =
                  level.id !== undefined && level.id !== null
                    ? level.id.toString()
                    : ''
                return (
                  <MenuItem
                    key={level.id || levelId}
                    value={level.name || level.level}
                  >
                    {level.name || level.level}
                  </MenuItem>
                )
              })}
            </Select>
          </Box>

          {/* Assessment Status Filter - Label (Row 1) */}
          <Box
            className="filter-group-label"
            sx={{ gridRow: 1, gridColumn: 2 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#374151',
                mb: '2px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Assessment Status
            </Typography>
          </Box>

          {/* Assessment Status Filter - Dropdown (Row 2) */}
          <Box
            className="filter-group-input"
            sx={{ gridRow: 2, gridColumn: 2 }}
          >
            <Select
              value={assessmentStatusFilter}
              onChange={(e) => setAssessmentStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                ...(assessmentStatusFilter && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  backgroundColor: '#eff6ff',
                }),
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                },
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="0">None</MenuItem>
              <MenuItem value="1">Prequalifying</MenuItem>
              <MenuItem value="2">Prequalified</MenuItem>
              <MenuItem value="3">Awaiting Review</MenuItem>
              <MenuItem value="4">Completed</MenuItem>
            </Select>
          </Box>

          {/* Assessor Filter - Label (Row 1) */}
          <Box
            className="filter-group-label"
            sx={{ gridRow: 1, gridColumn: 3 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#374151',
                mb: '2px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Assessor
            </Typography>
          </Box>

          {/* Assessor Filter - Dropdown (Row 2) */}
          <Box
            className="filter-group-input"
            sx={{ gridRow: 2, gridColumn: 3 }}
          >
            <Select
              value={assessorFilter}
              onChange={(e) => setAssessorFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                ...(assessorFilter && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  backgroundColor: '#eff6ff',
                }),
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                },
              }}
            >
              <MenuItem value="">All assessors</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Last Assessment Filter - Label (Row 1) */}
          <Box
            className="filter-group-label"
            sx={{ gridRow: 1, gridColumn: 4 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#374151',
                mb: '2px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Last Assessment
            </Typography>
          </Box>

          {/* Last Assessment Filter - Date Picker (Row 2) */}
          <Box
            className="filter-group-input"
            sx={{ gridRow: 2, gridColumn: 4 }}
          >
            <Select
              value={lastAssessmentFilter}
              onChange={(e) => setLastAssessmentFilter(e.target.value)}
              displayEmpty
              size="small"
              renderValue={(selected) => {
                return selected
                  ? new Date(selected).toLocaleDateString()
                  : 'All dates'
              }}
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9ca3af',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                ...(lastAssessmentFilter && {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  backgroundColor: '#eff6ff',
                }),
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '8px',
                    boxShadow:
                      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                },
              }}
            >
              <MenuItem value="">All dates</MenuItem>
              <MenuItem value={new Date().toISOString().split('T')[0]}>
                Today
              </MenuItem>
              <MenuItem
                value={
                  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                }
              >
                Last 7 days
              </MenuItem>
              <MenuItem
                value={
                  new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                }
              >
                Last 30 days
              </MenuItem>
              <MenuItem
                value={
                  new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                }
              >
                Last 90 days
              </MenuItem>
              <MenuItem
                value={
                  new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0]
                }
              >
                Last year
              </MenuItem>
            </Select>
          </Box>

          {/* Empty space for buttons in Row 1 */}
          <Box
            className="filter-group-label"
            sx={{ gridRow: 1, gridColumn: 5 }}
          >
            {/* Empty space */}
          </Box>

          {/* Filter Actions in Row 2 */}
          <Box
            className="filter-actions"
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start', // Align items to the top to match other components
              justifyContent: 'flex-start',
              gridRow: 2,
              gridColumn: 5,
            }}
          >
            {/* Apply Button */}
            <MuiButton
              variant="contained"
              onClick={applyFilters}
              sx={{
                backgroundColor: '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                padding: '0 16px',
                height: '40px', // Match height of other components
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#2563eb',
                },
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flex: { xs: 1, md: 'none' },
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              Apply
            </MuiButton>

            {/* Reset Button */}
            <MuiButton
              variant="outlined"
              onClick={() => {
                setRiskLevelFilter('')
                setAssessmentStatusFilter('')
                setAssessorFilter('')
                setLastAssessmentFilter('')
                // Apply the cleared filters immediately
                setFilterValues({
                  include_risk_assessment: true,
                  risk_level: '',
                  assessment_status: '',
                  risk_assessor_id: '',
                  last_assessment: '',
                })
                refresh() // Refresh the list immediately
              }}
              sx={{
                backgroundColor: 'white',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                padding: '0 16px',
                height: '40px', // Match height of other components
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f9fafb',
                  color: '#374151',
                },
                flex: { xs: 1, md: 'none' },
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              Reset
            </MuiButton>
          </Box>
        </Box>

        <List
          key={JSON.stringify(filterValues)} // Add key prop to force re-render when filterValues changes
          resource="vendors"
          filter={filterValues}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={10}
          actions={false}
          bulkActionButtons={false}
          pagination={<Pagination />}
          // Update vendorCount when data is loaded
          queryOptions={{
            onSuccess: (data) => {
              if (data && data.total !== undefined) {
                setVendorCount(data.total)
              }
            },
          }}
          sx={{
            mt: 0,
            '& .MuiToolbar-root:not(.RaPaginationToolbar-toolbar)': {
              height: 20,
              minHeight: 20,
              padding: 0,
              overflow: 'hidden',
              display: 'auto',
            },
            '& .RaFilterForm-root': {
              display: 'none',
            },
            // Enhanced table styling
            '& .RaDatagrid-table': {
              borderCollapse: 'separate',
              borderSpacing: 0,
            },
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#f8fafc',
              padding: '16px',
              fontWeight: 'medium',
              color: '#6b7280',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: '1px solid #e2e8f0',
            },
            '& .RaDatagrid-rowCell': {
              padding: '16px',
              verticalAlign: 'middle',
              borderBottom: '1px solid #f1f5f9',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#f8fafc',
              },
              transition: 'background-color 0.2s ease',
            },
          }}
        >
          <Datagrid
            bulkActionButtons={false}
            sx={{
              mt: 0,
              '& .RaDatagrid-rowCell': {
                padding: '16px',
                verticalAlign: 'middle',
              },
              '& .RaDatagrid-headerCell': {
                padding: '16px',
                backgroundColor: '#f9fafb',
                fontWeight: 'medium',
                color: '#6b7280',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
              },
              '& .RaDatagrid-row': {
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                transition: 'background-color 0.2s ease',
              },
              '& .RaDatagrid-tbody': {
                '& tr': {
                  borderBottom: '1px solid #e5e7eb',
                },
              },
              '& .MuiTableCell-root': {
                borderBottom: 'none',
              },
              '& .MuiButton-root': {
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 'medium',
              },
            }}
          >
            {/* Custom checkbox field for selecting vendors with select all header */}
            <FunctionField
              source="id"
              sortable={false}
              label={
                <SelectAllHeader
                  selectedVendors={selectedVendors}
                  setSelectedVendors={setSelectedVendors}
                  setVendorListData={setVendorListData}
                />
              }
              render={(record) => {
                const isSelected = selectedVendors.includes(record.id)
                return (
                  <MuiCheckbox
                    checked={isSelected}
                    onChange={() => {
                      if (isSelected) {
                        setSelectedVendors(
                          selectedVendors.filter((id) => id !== record.id)
                        )
                      } else {
                        setSelectedVendors([...selectedVendors, record.id])
                      }
                    }}
                  />
                )
              }}
            />
            <TextField
              source="name"
              sx={{
                '& span': {
                  fontWeight: 'medium',
                },
              }}
            />
            <RiskLevelField
              source="risk_level"
              label="Risk Level"
              riskLevelsData={riskLevels}
              loading={loading}
            />
            <AssessmentStatusField
              source="assessment_status"
              label="Assessment Status"
            />
            <ComplianceScoreField
              source="compliance_score"
              label="Compliance Score"
              riskLevelsData={riskLevels}
              loading={loading}
            />
            <AssessorField source="id" label="Assessor" />
            <LastAssessmentField
              source="last_assessment"
              label="Last Assessment"
            />
            <QuestionnairesField source="id" label="Questionnaires" />
          </Datagrid>
        </List>
      </Paper>

      {/* Questionnaire Templates Section */}
      <Paper
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: templatesExpanded ? '1px solid #e5e7eb' : 'none',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: '#f9fafb',
            },
            transition: 'background-color 0.2s ease',
          }}
          onClick={() => setTemplatesExpanded(!templatesExpanded)}
        >
          <Typography variant="h6">Questionnaire Templates</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {questionnaires.length} templates available
            </Typography>
            <IconButton size="small">
              {templatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={templatesExpanded}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Prequalification Questionnaires Card */}
              <Grid item xs={12} sm={6}>
                <Paper
                  sx={{
                    p: 3,
                    border: '2px solid',
                    borderColor:
                      activeTemplateType === QuestionnaireType.PRELIMINARY
                        ? 'primary.main'
                        : '#e5e7eb',
                    bgcolor:
                      activeTemplateType === QuestionnaireType.PRELIMINARY
                        ? '#f0f7ff'
                        : 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor:
                        activeTemplateType === QuestionnaireType.PRELIMINARY
                          ? 'primary.main'
                          : '#d1d5db',
                    },
                  }}
                  onClick={() =>
                    setActiveTemplateType(
                      activeTemplateType === QuestionnaireType.PRELIMINARY
                        ? null
                        : QuestionnaireType.PRELIMINARY
                    )
                  }
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}
                  >
                    <FactCheckIcon
                      sx={{ fontSize: 48, color: 'text.secondary' }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Prequalification Questionnaires
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {
                          questionnaires.filter(
                            (q) => q.type === QuestionnaireType.PRELIMINARY
                          ).length
                        }{' '}
                        questionnaire templates available
                      </Typography>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                        }}
                      >
                        View Templates (
                        {
                          questionnaires.filter(
                            (q) => q.type === QuestionnaireType.PRELIMINARY
                          ).length
                        }
                        )
                      </MuiButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Risk Assessments Card */}
              <Grid item xs={12} sm={6}>
                <Paper
                  sx={{
                    p: 3,
                    border: '2px solid',
                    borderColor:
                      activeTemplateType === QuestionnaireType.RISK
                        ? 'primary.main'
                        : '#e5e7eb',
                    bgcolor:
                      activeTemplateType === QuestionnaireType.RISK
                        ? '#f0f7ff'
                        : 'white',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor:
                        activeTemplateType === QuestionnaireType.RISK
                          ? 'primary.main'
                          : '#d1d5db',
                    },
                  }}
                  onClick={() =>
                    setActiveTemplateType(
                      activeTemplateType === QuestionnaireType.RISK
                        ? null
                        : QuestionnaireType.RISK
                    )
                  }
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}
                  >
                    <AssessmentIcon
                      sx={{ fontSize: 48, color: 'text.secondary' }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        Risk Assessments
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {
                          questionnaires.filter(
                            (q) => q.type === QuestionnaireType.RISK
                          ).length
                        }{' '}
                        questionnaire templates available
                      </Typography>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                        }}
                      >
                        View Templates (
                        {
                          questionnaires.filter(
                            (q) => q.type === QuestionnaireType.RISK
                          ).length
                        }
                        )
                      </MuiButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Template Directory Section */}
      <Collapse
        in={templatesExpanded && activeTemplateType !== null}
        sx={{ mt: 3 }}
      >
        <Paper
          sx={{
            p: 3,
            width: '100%',
            borderRadius: 2,
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          {activeTemplateType === QuestionnaireType.PRELIMINARY && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="h6">
                  Prequalification Questionnaire Templates
                </Typography>
                <MuiButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewFormType(QuestionnaireType.PRELIMINARY)
                    setIsCreateDialogOpen(true)
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  Create New
                </MuiButton>
              </Box>

              {loadingQuestionnaires ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : questionnaires.filter(
                  (q) => q.type === QuestionnaireType.PRELIMINARY
                ).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  No prequalification questionnaires available
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {questionnaires
                    .filter((q) => q.type === QuestionnaireType.PRELIMINARY)
                    .map((questionnaire) => (
                      <Paper
                        key={questionnaire.id}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          border: '1px solid #e5e7eb',
                          '&:hover': {
                            bgcolor: '#f9fafb',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {questionnaire.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              {questionnaire.description}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <QuickSelectButton
                              questionnaire={questionnaire}
                              selectedVendors={selectedVendors}
                              vendorListData={vendorListData}
                              checkVendorsAssessmentStatus={
                                checkVendorsAssessmentStatus
                              }
                              handleQuickSelectClick={handleQuickSelectClick}
                            />
                            <IconButton
                              size="small"
                              aria-label="view"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewQuestionnaire(questionnaire)
                              }}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="duplicate"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicateQuestionnaire(questionnaire.id)
                              }}
                              disabled={isDuplicating}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="edit"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditForm(questionnaire)
                              }}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="delete"
                              onClick={(e) => {
                                e.stopPropagation()
                                showDeleteConfirmation(questionnaire.id)
                              }}
                              disabled={isDeleting}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {activeTemplateType === QuestionnaireType.RISK && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="h6">Risk Assessment Templates</Typography>
                <MuiButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewFormType(QuestionnaireType.RISK)
                    setIsCreateDialogOpen(true)
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  Create New
                </MuiButton>
              </Box>

              {loadingQuestionnaires ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : questionnaires.filter(
                  (q) => q.type === QuestionnaireType.RISK
                ).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  No risk assessment questionnaires available
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {questionnaires
                    .filter((q) => q.type === QuestionnaireType.RISK)
                    .map((questionnaire) => (
                      <Paper
                        key={questionnaire.id}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          border: '1px solid #e5e7eb',
                          '&:hover': {
                            bgcolor: '#f9fafb',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {questionnaire.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              {questionnaire.description}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <QuickSelectButton
                              questionnaire={questionnaire}
                              selectedVendors={selectedVendors}
                              vendorListData={vendorListData}
                              checkVendorsAssessmentStatus={
                                checkVendorsAssessmentStatus
                              }
                              handleQuickSelectClick={handleQuickSelectClick}
                            />
                            <IconButton
                              size="small"
                              aria-label="view"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewQuestionnaire(questionnaire)
                              }}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="duplicate"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicateQuestionnaire(questionnaire.id)
                              }}
                              disabled={isDuplicating}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="edit"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditForm(questionnaire)
                              }}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="delete"
                              onClick={(e) => {
                                e.stopPropagation()
                                showDeleteConfirmation(questionnaire.id)
                              }}
                              disabled={isDeleting}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Questionnaires to be Reviewed Section */}
      <Paper
        sx={{
          mt: 2,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: reviewQuestionnairesExpanded
              ? '1px solid #e5e7eb'
              : 'none',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: '#f9fafb',
            },
            transition: 'background-color 0.2s ease',
          }}
          onClick={() =>
            setReviewQuestionnairesExpanded(!reviewQuestionnairesExpanded)
          }
        >
          <Typography variant="h6">Questionnaires to be Reviewed</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {reviewQuestionnaires.length} questionnaires awaiting review
            </Typography>
            <IconButton size="small">
              {reviewQuestionnairesExpanded ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={reviewQuestionnairesExpanded}>
          <Box sx={{ p: 3 }}>
            {loadingReviewQuestionnaires ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : reviewQuestionnaires.length === 0 ? (
              <Box
                padding={2}
                bgcolor="#f5f5f5"
                borderRadius={1}
                textAlign="center"
              >
                No questionnaires awaiting your review.
              </Box>
            ) : (
              <Box>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Prequalification Questionnaires Card */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        border: '2px solid',
                        borderColor:
                          activeReviewType === QuestionnaireType.PRELIMINARY
                            ? 'primary.main'
                            : '#e5e7eb',
                        bgcolor:
                          activeReviewType === QuestionnaireType.PRELIMINARY
                            ? '#f0f7ff'
                            : 'white',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor:
                            activeReviewType === QuestionnaireType.PRELIMINARY
                              ? 'primary.main'
                              : '#d1d5db',
                        },
                      }}
                      onClick={() =>
                        setActiveReviewType(
                          activeReviewType === QuestionnaireType.PRELIMINARY
                            ? null
                            : QuestionnaireType.PRELIMINARY
                        )
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 3,
                        }}
                      >
                        <FactCheckIcon
                          sx={{ fontSize: 48, color: 'text.secondary' }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            Prequalification
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {
                              reviewQuestionnaires.filter(
                                (q) =>
                                  q.questionnaire?.type ===
                                  QuestionnaireType.PRELIMINARY
                              ).length
                            }{' '}
                            questionnaires awaiting review
                          </Typography>
                          <MuiButton
                            variant="contained"
                            color="primary"
                            sx={{
                              mt: 1,
                              textTransform: 'none',
                              borderRadius: 1,
                              px: 2,
                              py: 1,
                            }}
                          >
                            View Questionnaires (
                            {
                              reviewQuestionnaires.filter(
                                (q) =>
                                  q.questionnaire?.type ===
                                  QuestionnaireType.PRELIMINARY
                              ).length
                            }
                            )
                          </MuiButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Risk Questionnaires Card */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      sx={{
                        p: 3,
                        border: '2px solid',
                        borderColor:
                          activeReviewType === QuestionnaireType.RISK
                            ? 'primary.main'
                            : '#e5e7eb',
                        bgcolor:
                          activeReviewType === QuestionnaireType.RISK
                            ? '#f0f7ff'
                            : 'white',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor:
                            activeReviewType === QuestionnaireType.RISK
                              ? 'primary.main'
                              : '#d1d5db',
                        },
                      }}
                      onClick={() =>
                        setActiveReviewType(
                          activeReviewType === QuestionnaireType.RISK
                            ? null
                            : QuestionnaireType.RISK
                        )
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 3,
                        }}
                      >
                        <AssessmentIcon
                          sx={{ fontSize: 48, color: 'text.secondary' }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            Risk
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {
                              reviewQuestionnaires.filter(
                                (q) =>
                                  q.questionnaire?.type ===
                                  QuestionnaireType.RISK
                              ).length
                            }{' '}
                            questionnaires awaiting review
                          </Typography>
                          <MuiButton
                            variant="contained"
                            color="primary"
                            sx={{
                              mt: 1,
                              textTransform: 'none',
                              borderRadius: 1,
                              px: 2,
                              py: 1,
                            }}
                          >
                            View Questionnaires (
                            {
                              reviewQuestionnaires.filter(
                                (q) =>
                                  q.questionnaire?.type ===
                                  QuestionnaireType.RISK
                              ).length
                            }
                            )
                          </MuiButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Review Questionnaires Detail Section */}
      <Collapse
        in={reviewQuestionnairesExpanded && activeReviewType !== null}
        sx={{ mt: 3 }}
      >
        <Paper
          sx={{
            p: 3,
            width: '100%',
            borderRadius: 2,
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          {activeReviewType === QuestionnaireType.PRELIMINARY && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="h6">
                  Prequalification Questionnaires Awaiting Review
                </Typography>
              </Box>

              {loadingReviewQuestionnaires ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : reviewQuestionnaires.filter(
                  (q) => q.questionnaire?.type === QuestionnaireType.PRELIMINARY
                ).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  No prequalification questionnaires awaiting review
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Questionnaire</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Completed At</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reviewQuestionnaires
                        .filter(
                          (q) =>
                            q.questionnaire?.type ===
                            QuestionnaireType.PRELIMINARY
                        )
                        .map((questionnaire) => (
                          <TableRow key={questionnaire.id}>
                            <TableCell>
                              {questionnaire.vendor?.name || 'Unknown Vendor'}
                            </TableCell>
                            <TableCell>
                              {questionnaire.questionnaire?.name ||
                                'Unknown Questionnaire'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  questionnaire.status ===
                                  VendorQuestionnaireStatuses.VendorSubmitted
                                    ? 'Submitted'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Completed
                                    ? 'Completed'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Cancelled
                                    ? 'Cancelled'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Rejected
                                    ? 'Rejected'
                                    : 'Unknown'
                                }
                                color={
                                  questionnaire.status ===
                                  VendorQuestionnaireStatuses.VendorSubmitted
                                    ? 'warning'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Completed
                                    ? 'success'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Cancelled
                                    ? 'error'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Rejected
                                    ? 'error'
                                    : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {questionnaire.completed_at
                                ? new Date(
                                    questionnaire.completed_at
                                  ).toLocaleDateString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => {
                                  setSelectedReviewQuestionnaire(questionnaire)
                                  setReviewDialogOpen(true)
                                }}
                                title="View"
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {activeReviewType === QuestionnaireType.RISK && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Typography variant="h6">
                  Risk Questionnaires Awaiting Review
                </Typography>
              </Box>

              {loadingReviewQuestionnaires ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : reviewQuestionnaires.filter(
                  (q) => q.questionnaire?.type === QuestionnaireType.RISK
                ).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  No risk questionnaires awaiting review
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Questionnaire</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Completed At</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reviewQuestionnaires
                        .filter(
                          (q) =>
                            q.questionnaire?.type === QuestionnaireType.RISK
                        )
                        .map((questionnaire) => (
                          <TableRow key={questionnaire.id}>
                            <TableCell>
                              {questionnaire.vendor?.name || 'Unknown Vendor'}
                            </TableCell>
                            <TableCell>
                              {questionnaire.questionnaire?.name ||
                                'Unknown Questionnaire'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  questionnaire.status ===
                                  VendorQuestionnaireStatuses.VendorSubmitted
                                    ? 'Submitted'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Completed
                                    ? 'Completed'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Cancelled
                                    ? 'Cancelled'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Rejected
                                    ? 'Rejected'
                                    : 'Unknown'
                                }
                                color={
                                  questionnaire.status ===
                                  VendorQuestionnaireStatuses.VendorSubmitted
                                    ? 'warning'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Completed
                                    ? 'success'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Cancelled
                                    ? 'error'
                                    : questionnaire.status ===
                                      VendorQuestionnaireStatuses.Rejected
                                    ? 'error'
                                    : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {questionnaire.completed_at
                                ? new Date(
                                    questionnaire.completed_at
                                  ).toLocaleDateString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {questionnaire.score !== undefined
                                ? `${questionnaire.score} (${questionnaire.percentage_score}%)`
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => {
                                  setSelectedReviewQuestionnaire(questionnaire)
                                  setReviewDialogOpen(true)
                                }}
                                title="View"
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Questionnaire Management Button */}
      {/* <Box sx={{ mt: 6, mb: 4, display: 'flex', justifyContent: 'center' }}> */}
      {/*  <Link */}
      {/*    to="/admin/risk/questionnaires" */}
      {/*    style={{ textDecoration: 'none' }} */}
      {/*  > */}
      {/*    <MuiButton */}
      {/*      variant="contained" */}
      {/*      color="primary" */}
      {/*      startIcon={<QuizIcon />} */}
      {/*      sx={{ */}
      {/*        textTransform: 'none', */}
      {/*        borderRadius: 1, */}
      {/*        px: 4, */}
      {/*        py: 1.5, */}
      {/*        fontSize: '1rem', */}
      {/*        fontWeight: 'medium', */}
      {/*        boxShadow: */}
      {/*          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', */}
      {/*      }} */}
      {/*    > */}
      {/*      Questionnaire Management */}
      {/*    </MuiButton> */}
      {/*  </Link> */}
      {/* </Box> */}

      {/* Create Form Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false)
          resetCreateForm()
        }}
        aria-labelledby="create-form-dialog-title"
      >
        <DialogTitle id="create-form-dialog-title">
          Create New Questionnaire
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="questionnaire-type-label">
              Questionnaire Type
            </InputLabel>
            <Select
              labelId="questionnaire-type-label"
              id="questionnaire-type"
              value={newFormType}
              label="Questionnaire Type"
              onChange={(e) =>
                setNewFormType(e.target.value as QuestionnaireType)
              }
            >
              <MenuItem value={QuestionnaireType.PRELIMINARY}>
                Prequalification Questionnaire
              </MenuItem>
              <MenuItem value={QuestionnaireType.RISK}>
                Risk Assessment
              </MenuItem>
            </Select>
            <FormHelperText>
              {newFormType === QuestionnaireType.PRELIMINARY
                ? 'Prequalification Questionnaires have a maximum of 10 questions with Yes/No answers.'
                : 'Risk Assessments have a maximum of 100 questions with dropdown answers.'}
            </FormHelperText>
          </FormControl>
          <MuiTextField
            autoFocus
            margin="dense"
            id="name"
            label="Questionnaire Name"
            type="text"
            fullWidth
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
          />
          <MuiTextField
            margin="dense"
            id="description"
            label="Questionnaire Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newFormDescription}
            onChange={(e) => setNewFormDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsCreateDialogOpen(false)
              resetCreateForm()
            }}
            disabled={isCreating}
          />
          <Button
            label={isCreating ? 'Creating...' : 'Create'}
            onClick={handleCreateForm}
            disabled={isCreating || !newFormName.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* View Questionnaire Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="view-questionnaire-dialog-title"
      >
        <DialogTitle id="view-questionnaire-dialog-title">
          View Questionnaire: {questionnaireToView?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseViewModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {questionnaireToView && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {questionnaireToView.name}
              </Typography>
              <Typography variant="body1" paragraph>
                {questionnaireToView.description}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Type:{' '}
                {questionnaireToView.type === QuestionnaireType.PRELIMINARY
                  ? 'Prequalification Questionnaire'
                  : 'Risk Assessment'}
              </Typography>
              {questionnaireToView.questionnaire && (
                <Box sx={{ mt: 3 }}>
                  {questionnaireToView.questionnaire.map((section, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {section.title || `Section ${index + 1}`}
                      </Typography>
                      {section.fields &&
                        section.fields.map((field, fieldIndex) => (
                          <Box key={fieldIndex} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">
                              {field.label}
                              {field.required && (
                                <span style={{ color: 'red' }}> *</span>
                              )}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {field.fieldType === 'yesno'
                                ? 'Yes/No question'
                                : field.fieldType === 'attachment'
                                ? 'Attachment question'
                                : field.fieldType === 'text'
                                ? 'Text Box question'
                                : 'Dropdown question'}
                            </Typography>
                            {field.fieldType === 'dropdown' &&
                              field.options && (
                                <Box sx={{ mt: 1, ml: 2 }}>
                                  {field.options.map((option, optionIndex) => (
                                    <Typography
                                      key={optionIndex}
                                      variant="body2"
                                    >
                                      • {option.text}
                                      {option.score !== undefined &&
                                        ` (Score: ${option.score})`}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        ))}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button label="Close" onClick={handleCloseViewModal} />
        </DialogActions>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="edit-form-dialog-title"
      >
        <DialogTitle id="edit-form-dialog-title">
          Edit Questionnaire: {questionnaireToEdit?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseEditModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {questionnaireToEdit && (
            <OrganisationRiskQuestionnaire
              questionnaireId={questionnaireToEdit.id}
              isModal
              onClose={handleCloseEditModal}
              initialQuestionnaireType={questionnaireToEdit.type}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button label="Close" onClick={handleCloseEditModal} />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setQuestionnaireToDelete(null)
        }}
        aria-labelledby="delete-questionnaire-dialog-title"
      >
        <DialogTitle id="delete-questionnaire-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this questionnaire? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            onClick={() => {
              setIsDeleteDialogOpen(false)
              setQuestionnaireToDelete(null)
            }}
            disabled={isDeleting}
          />
          <Button
            label={isDeleting ? 'Deleting...' : 'Delete'}
            onClick={handleDeleteForm}
            disabled={isDeleting}
            color="primary"
          />
        </DialogActions>
      </Dialog>

      {/* Quick Select Modal */}
      <Dialog
        open={quickSelectModalOpen}
        onClose={() => {
          setQuickSelectModalOpen(false)
          setSendingProgress(0)
          setTotalToSend(0)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send {selectedQuestionnaire?.name}{' '}
          {selectedQuestionnaire?.type === QuestionnaireType.PRELIMINARY
            ? 'Prequalification'
            : 'Risk Assessment'}
          ?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              You are about to send this questionnaire to{' '}
              {selectedVendors.length} selected vendor(s).
            </Typography>

            {sendingQuestionnaires && (
              <Box sx={{ width: '100%', mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Sending progress: {sendingProgress} of {totalToSend}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(sendingProgress / totalToSend) * 100}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="assessor-select-label">Assessor</InputLabel>
              <Select
                labelId="assessor-select-label"
                value={selectedAssessor}
                onChange={(e) => setSelectedAssessor(e.target.value)}
                label="Assessor"
                disabled={loadingUsers}
              >
                {loadingUsers ? (
                  <MenuItem value="">
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="email-field-select-label">Email Field</InputLabel>
              <Select
                labelId="email-field-select-label"
                value={selectedEmailField}
                onChange={(e) => setSelectedEmailField(e.target.value)}
                label="Email Field"
              >
                {emailFields.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => setQuickSelectModalOpen(false)}
            color="inherit"
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleSendQuestionnaires}
            variant="contained"
            color="primary"
            disabled={
              !selectedAssessor || !selectedEmailField || sendingQuestionnaires
            }
            startIcon={
              sendingQuestionnaires ? (
                <CircularProgress size={20} />
              ) : (
                <SendIcon />
              )
            }
          >
            {sendingQuestionnaires ? 'Sending...' : 'Send'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Questionnaire Review Dialog */}
      <QuestionnaireReviewForm
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        selectedQuestionnaire={selectedReviewQuestionnaire}
        viewSource="review"
        onAccept={() => {
          setReviewQuestionnaires((prevQuestionnaires) =>
            prevQuestionnaires.filter(
              (q) => q.id !== selectedReviewQuestionnaire?.id
            )
          )
          loadReviewQuestionnaires()
          // Refresh the vendor list to show updated assessment status
          refresh()
        }}
        onReject={() => {
          setReviewQuestionnaires((prevQuestionnaires) =>
            prevQuestionnaires.filter(
              (q) => q.id !== selectedReviewQuestionnaire?.id
            )
          )
          loadReviewQuestionnaires()
          // Refresh the vendor list to show updated assessment status
          refresh()
        }}
        onRefresh={() => {
          loadReviewQuestionnaires()
          // Refresh the vendor list to show updated assessment status
          refresh()
        }}
      />

      {/* Vendor Details Dialog */}
      <QuestionnaireReviewForm
        open={vendorDetailsDialogOpen}
        onClose={() => setVendorDetailsDialogOpen(false)}
        selectedQuestionnaire={selectedQuestionnaire}
        viewSource="vendor"
        onRefresh={() => {
          // Refresh the vendor list to show updated assessment status
          refresh()
        }}
      />
    </section>
  )
}

// Wrap the RiskManagement component with a provider for the RiskManagementContext
const RiskManagementWithContext = () => {
  const [vendorDetailsDialogOpen, setVendorDetailsDialogOpen] = useState(false)
  const [selectedVendorId, setSelectedVendorId] = useState<
    string | number | null
  >(null)
  const refresh = useRefresh()

  const contextValue = useMemo(
    () => ({
      vendorDetailsDialogOpen,
      setVendorDetailsDialogOpen,
      selectedVendorId,
      setSelectedVendorId,
    }),
    [vendorDetailsDialogOpen, selectedVendorId]
  )

  return (
    <RiskManagementContext.Provider value={contextValue}>
      <RiskManagement />
      <VendorDetailsDialog
        open={vendorDetailsDialogOpen}
        onClose={() => setVendorDetailsDialogOpen(false)}
        vendorId={selectedVendorId}
        onChanges={refresh}
      />
    </RiskManagementContext.Provider>
  )
}

export default RiskManagementWithContext
